/**
 * MIRRORS `src/lib/pii-redaction.ts`, which carries the reasoning. Deliberate second copy
 * — the frontend cannot import the backend build. `REDACTION_RULES` pins the two.
 */

/** Beyond this, text is narrative rather than diagnostic. */
export const MAX_TEXT_LENGTH = 500;

export const TRUNCATION_MARKER = '…<truncated>';

const RULES: ReadonlyArray<readonly [RegExp, string]> = [
  // Email first: it contains digits that later rules would otherwise claim.
  [/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '<email>'],
  // \b cannot precede '+', so the boundary attaches to the 0-prefixed form only.
  [/(?:\+44\s?|\b0)7\d{3}[\s-]?\d{6}\b/g, '<phone>'],
  // NHS number and 10-digit phone forms. \b stops it claiming a 13-digit epoch.
  [/\b\d{3}[\s-]?\d{3}[\s-]?\d{4}\b/g, '<id-number>'],
  // The space is required: without it the rule eats nanoids, and a case id is the most
  // useful thing in a fault report. An unspaced postcode is not redacted.
  [/\b[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}\b/g, '<postcode>'],
];

export function redactText(input: string): string {
  let out = input;
  for (const [pattern, replacement] of RULES) out = out.replace(pattern, replacement);
  return out.length > MAX_TEXT_LENGTH
    ? `${out.slice(0, MAX_TEXT_LENGTH)}${TRUNCATION_MARKER}`
    : out;
}

/** Pattern sources, exported so the two copies can be compared rather than trusted. */
export const REDACTION_RULES: ReadonlyArray<readonly [string, string]> = RULES.map(
  ([re, tag]) => [String(re), tag] as const,
);

/** Redacts strings anywhere in a value, leaving structure and non-strings intact. */
export function redactDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return redactText(value);
  if (depth >= 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = redactDeep(v, depth + 1);
  }
  return out;
}
